import { inject, Injectable, signal } from "@angular/core";
import { TourService } from "ngx-ui-tour-md-menu";
import { Subject } from "rxjs";
import { DesignerTourSteps, MyIStepOption } from "~/src/app/shared/models/designer-tour.model";
import { UserService } from "./user/user.service";

@Injectable({
    providedIn: "root",
})
export class CustomTourService {
    private tourService = inject(TourService);

    private openActionsCallSubject = new Subject<boolean>();
    public openActionsCall$ = this.openActionsCallSubject.asObservable();

    private gifClickSubject = new Subject<string>();
    public gifClicked$ = this.gifClickSubject.asObservable();

    public stepShow$ = this.tourService.stepShow$;
    public stepHide$ = this.tourService.stepHide$;
    public initialize$ = this.tourService.initialize$;
    public start$ = this.tourService.start$;
    public end$ = this.tourService.end$;
    public pause$ = this.tourService.pause$;
    public resume$ = this.tourService.resume$;
    public hasCompletedTour = signal(false);
    public isDirectTourSearchSignal = signal(false);

    public designerTourSteps: {
        step: MyIStepOption;
        label: string;
        icon: string;
    }[] = [
        { step: DesignerTourSteps.compass, label: "Understanding the compass", icon: "explore" },

        { step: DesignerTourSteps.copy, label: "Copying shapes", icon: "content_copy" },
        { step: DesignerTourSteps.paste, label: "Pasting shapes", icon: "content_paste" },
        { step: DesignerTourSteps.duplicate, label: "Duplicating shapes", icon: "library_add" },

        { step: DesignerTourSteps.undo, label: "Undoing actions", icon: "undo" },
        { step: DesignerTourSteps.redo, label: "Redoing actions", icon: "redo" },
        { step: DesignerTourSteps.deleteShape, label: "Deleting shapes", icon: "delete" },

        { step: DesignerTourSteps.zoomIn, label: "Zooming in on the design", icon: "zoom_in" },
        { step: DesignerTourSteps.zoomOut, label: "Zooming out of the design", icon: "zoom_out" },

        { step: DesignerTourSteps.shapeSelector, label: "Selecting shapes", icon: "category" },
        { step: DesignerTourSteps.clickSelection, label: "Using click to select shapes", icon: "mouse" },
        { step: DesignerTourSteps.marqueeSelection, label: "Selecting multiple shapes with marquee", icon: "select_all" },

        { step: DesignerTourSteps.dragDrop, label: "Dragging and dropping shapes", icon: "open_with" },

        { step: DesignerTourSteps.actions, label: "Understanding the actions menu", icon: "settings" },
        { step: DesignerTourSteps.flips, label: "Flipping shapes", icon: "flip" },

        { step: DesignerTourSteps.colourPicker, label: "Applying colours to shapes", icon: "palette" },

        { step: DesignerTourSteps.getCount, label: "Checking shape count", icon: "pin" },
        { step: DesignerTourSteps.totalBlocks, label: "Viewing total block count", icon: "view_in_ar" },

        { step: DesignerTourSteps.saveButton, label: "Saving your structure", icon: "save" },
        { step: DesignerTourSteps.publishButton, label: "Publishing your structure", icon: "publish" },
        { step: DesignerTourSteps.loadButton, label: "Load All your structure", icon: "download" },

        { step: DesignerTourSteps.guide, label: "Using the guide feature", icon: "help_outline" },
    ];

    constructor(private userService: UserService) {}

    public startDesignerTour(): void {
        const stepsOnly = this.designerTourSteps.map((item) => item.step);
        this.initializeTour(stepsOnly);
        this.tourService.disableHotkeys();
        this.start();
    }

    public start(): void {
        this.tourService.start();
    }

    public startAt(stepId: number | string): void {
        const stepsOnly = this.designerTourSteps.map((item) => item.step);
        this.initializeTour(stepsOnly);
        this.tourService.disableHotkeys();
        this.tourService.startAt(stepId);
    }

    public end(): void {
        this.tourService.end();
    }

    public pause(): void {
        this.tourService.pause();
    }

    public resume(): void {
        this.tourService.resume();
    }

    public next(): void {
        const currentStep = this.getCurrentStep();

        if (currentStep.anchorId === DesignerTourSteps.actions.anchorId) {
            this.openActions();
        }

        if (currentStep.anchorId === DesignerTourSteps.loadButton.anchorId) {
            this.closeActions();
        }

        this.tourService.next();
    }

    public prev(): void {
        const currentStep = this.getCurrentStep();
        if (currentStep.anchorId === DesignerTourSteps.flips.anchorId) {
            this.closeActions();
        }

        if (currentStep.anchorId === DesignerTourSteps.guide.anchorId) {
            this.openActions();
        }

        this.tourService.prev();
    }

    public hasPrev(stepId: MyIStepOption): boolean {
        return this.tourService.hasPrev(stepId);
    }

    public hasNext(stepId: MyIStepOption): boolean {
        return this.tourService.hasNext(stepId);
    }

    public get steps(): MyIStepOption[] {
        return this.tourService.steps;
    }

    public isLastStep(stepId: string): boolean {
        const lastStep = this.designerTourSteps[this.designerTourSteps.length - 1];
        return stepId === lastStep.step.anchorId;
    }

    public markTourAsCompleted(): void {}

    // PRIVATE FUNCTIONS

    private initializeTour(steps: MyIStepOption[]): void {
        this.tourService.initialize(steps, {
            enableBackdrop: true,
            allowUserInitiatedNavigation: true,
        });
    }

    public openActions(): void {
        this.openActionsCallSubject.next(true);
    }

    public closeActions(): void {
        this.openActionsCallSubject.next(false);
        this.isDirectTourSearchSignal.set(false);
    }

    private getCurrentStep(): MyIStepOption {
        return this.tourService.currentStep;
    }
}
